import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import setSearchTerm from "@tSchaedlerYesco/Store/Data/SearchTerm/Handlers/setSearchTerm";
import React, { FC } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel;
    extendedStyles?: ProductImageStyles;
    showSmallImage?: boolean;
}

type Props = OwnProps;

export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
}

export const productImageStyles: ProductImageStyles = {
    linkWrappingImage: {
        css: css`
            width: 100%;
        `,
    },
    image: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    img: {
        css: css`
            height: auto;
            width: 100%;
        `,
    },
};

const ProductImage: FC<Props> = ({ product, extendedStyles, showSmallImage }) => {
    const dispatch = useDispatch();
    const location = useAppSelector(state => getLocation(state));
    const pagination = useAppSelector(state => getProductListDataViewProperty(state, "pagination"));
    const { billToId, shipToId } = useAppSelector(state => getSession(state));
    const billToState = useAppSelector(state => getBillToState(state, billToId));
    const shipToState = useAppSelector(state => getShipToState(state, shipToId));
    const { value: shipTo } = shipToState;
    const { value: billTo } = billToState;

    const { query } = parseQueryString<{ query: string }>(location.search)

    const [styles] = React.useState(() => mergeToNew(productImageStyles, extendedStyles));

    let altText = "product" in product ? product.product.imageAltText : product.altText;
    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }

    const imagePath =
        "product" in product
            ? showSmallImage
                ? product.product.smallImagePath || product.product.mediumImagePath
                : product.product.mediumImagePath || product.product.smallImagePath
            : product.smallImagePath;

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const loading = product.idx === undefined ? "lazy" : product.idx < 3 ? "eager" : "lazy";

    const addSearchTerm = () => {
        if (query && billTo?.customerNumber !== undefined) {
            const totalItemCount = pagination?.totalItemCount;
            const productNumber = "product" in product ? product.product.productNumber : ""
            dispatch(setSearchTerm({
                searchTerm: {
                    searchedKeyword: query,
                    noOfProductResults: totalItemCount ?? 0,
                    customerBillToNumber: billTo.customerNumber,
                    customerShipToNumber: shipTo?.customerSequence ?? "",
                    visitedProductNumber: productNumber
                }
            }))
        }
    }

    return (
        <Clickable {...styles.linkWrappingImage} onClick={addSearchTerm} href={productDetailPath} data-test-selector="productImage">
            <Img {...styles.img} loading={loading} width="209" height="209" src={imagePath} altText={altText} />
        </Clickable>
    );
};

export default ProductImage;
